/**
 * Application Timeline panel (design §15; multi-applicant per the Figma
 * reference, QA 2026-07-15). Right-side sheet that slides in over a scrim:
 * header key-values, a "Select Applicant" chip row (one chip per
 * household member — hidden for solo applications), the SELECTED member's
 * per-event checklist, and a "View Application" jump to that member's own
 * detail page. Shared verbatim by the individual detail page, the combined
 * report, and the dashboard's `?view=timeline` deep link — the events come
 * pre-built per member (`MemberTimeline`, buildTimeline server-side with the
 * exact §15 title/by-line formats).
 *
 * House rules: DTO-only props, no useEffect — the selection state lives in
 * a child rendered inside SheetContent, so it resets naturally when the
 * sheet unmounts on close.
 */
import {
	Check,
	ChevronLeft,
	ChevronRight,
	CircleDashed,
	Clock,
	Send,
	X,
} from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router'
import { MemberAvatar } from '#app/components/application-detail-v2/vendor/MemberChip'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '#app/components/ui/sheet'
import { formatEventTimestamp } from '#app/utils/application-detail-v2/display'
import type {
	MemberTimeline,
	TimelineEvent,
} from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { createAvatarName } from '#app/utils/avatar-name'
import { cn } from '#app/utils/misc'

function EventRow({
	event,
	isLast,
}: {
	event: TimelineEvent
	isLast: boolean
}) {
	const timestamp = formatEventTimestamp(event.date)
	// Amber, non-check states: 'request' = an action the landlord SENT awaiting
	// fulfillment (guarantor / extra bank) → Send icon; 'pending' = a step in
	// progress / re-opened (e.g. income reset by an additional-bank request) →
	// Clock icon, so it doesn't read as "never started".
	const isRequest = event.kind === 'request'
	const isPending = event.kind === 'pending'
	const isAmber = isRequest || isPending
	// 'cancelled' (withdrawn/abandoned) = completed-but-not-approved → grey X.
	// 'danger' + completed (declined) = red X. Both override the green check.
	const isCancelled = event.kind === 'cancelled'
	const isDanger = event.completed && event.tone === 'danger'
	return (
		<li className="flex gap-4">
			<div className="flex flex-col items-center">
				<span
					className={cn(
						'flex size-10 shrink-0 items-center justify-center rounded-full',
						isAmber
							? 'bg-[#f59f0a] text-white'
							: isDanger
								? 'bg-[#dc2634] text-white'
								: isCancelled
									? 'bg-[#9ca3af] text-white'
									: event.completed
										? 'bg-[#16a371] text-white'
										: 'border-2 border-gray-200 bg-white text-gray-300',
					)}
				>
					{isRequest ? (
						<Send className="size-[18px]" />
					) : isPending ? (
						<Clock className="size-5" />
					) : isDanger || isCancelled ? (
						<X className="size-5" />
					) : event.completed ? (
						<Check className="size-5" />
					) : (
						<CircleDashed className="size-5" />
					)}
				</span>
				{isLast ? null : (
					<span
						className={cn(
							'w-[3px] flex-1',
							isAmber
								? 'bg-[#f59f0a]'
								: isDanger
									? 'bg-[#dc2634]'
									: isCancelled
										? 'bg-[#9ca3af]'
										: event.completed
											? 'bg-[#16a371]'
											: 'bg-[#ECECEA]',
						)}
						aria-hidden
					/>
				)}
			</div>
			<div className="pb-7">
				<p className="text-[16px] font-semibold text-[#32302F]">
					{event.title}
				</p>
				{event.note ? (
					<p className="mt-0.5 text-sm font-medium text-[#747474]">
						{event.note}
					</p>
				) : null}
				{event.byline ? (
					<p className="mt-0.5 text-sm font-medium text-[#747474]">
						{event.byline}
					</p>
				) : null}
				{timestamp ? (
					<p className="mt-0.5 text-sm text-[#747474]">{timestamp}</p>
				) : null}
			</div>
		</li>
	)
}

/** One Select-Applicant chip: the DASHBOARD's MemberAvatar (same
 * deriveMemberChipVisual legend — status ring, decision badges, guarantor
 * amber) so the selector can't drift from the member chips (QA 2026-07-15).
 * Selection reads on the PILL (dark outline), never on the avatar — a green
 * check badge there means "accepted" in the legend. */
function ApplicantChip({
	member,
	selected,
	onSelect,
}: {
	member: MemberTimeline
	selected: boolean
	onSelect: () => void
}) {
	return (
		<button
			type="button"
			onClick={onSelect}
			aria-pressed={selected}
			className={cn(
				'flex shrink-0 cursor-pointer items-center gap-2 rounded-full border py-1 pr-4 pl-1 transition-colors',
				selected
					? 'border-[#32302F]'
					: 'border-[#ECECEA] hover:border-[#CCCCCC]',
			)}
		>
			<MemberAvatar
				member={{
					kind:
						member.applicationType === 'guarantor' ? 'guarantor' : 'applicant',
					status: member.status,
					hasStarted: member.hasStarted,
					startedAt: member.startedAt,
					infoRequested: member.infoRequested,
					billingHeld: member.billingHeld,
					initials: createAvatarName(member.name),
				}}
			/>
			<span className="max-w-32 truncate text-sm font-medium text-[#32302F]">
				{member.name}
			</span>
		</button>
	)
}

function TimelineBody({
	members,
	defaultApplicationId,
	memberHref,
	hideViewApplicationFor,
}: {
	members: MemberTimeline[]
	defaultApplicationId: string
	/** Takes the MEMBER (not just the id) — cross-property households need
	 * each member's own propertyId in the View Application URL. */
	memberHref?: (member: MemberTimeline) => string
	hideViewApplicationFor?: string
}) {
	// Resets naturally: this component unmounts with the dialog (no
	// forceMount), so reopening re-seeds from the default.
	const [selectedId, setSelectedId] = useState(defaultApplicationId)
	// Chip row scrolls via arrows (scrollbar hidden). Ref-callback measure +
	// onScroll keep the arrows' disabled state in sync — no useEffect. Kept
	// ABOVE the early return so hooks stay unconditional.
	const chipScrollRef = useRef<HTMLDivElement>(null)
	const [chipEnds, setChipEnds] = useState({ left: false, right: false })
	const measureChips = useCallback((el: HTMLDivElement) => {
		const left = el.scrollLeft > 1
		const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
		setChipEnds((prev) =>
			prev.left === left && prev.right === right ? prev : { left, right },
		)
	}, [])
	const chipRefCb = useCallback(
		(el: HTMLDivElement | null) => {
			chipScrollRef.current = el
			if (el) measureChips(el)
		},
		[measureChips],
	)
	const scrollChips = (dir: 1 | -1) => {
		const el = chipScrollRef.current
		if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
	}
	const canScrollChips = chipEnds.left || chipEnds.right
	const selected =
		members.find((member) => member.applicationId === selectedId) ?? members[0]
	if (!selected) return null
	const href =
		memberHref && selected.applicationId !== hideViewApplicationFor
			? memberHref(selected)
			: null
	return (
		<>
			{members.length > 1 ? (
				<div className="mt-5 rounded-2xl border border-[#F3F3F3] p-4">
					<p className="font-regular text-[15px] text-[#32302F]">
						Select Applicant
					</p>
					{/* Arrows instead of a scrollbar (shown only when the chips
					    overflow); the scroll area itself hides its bar. */}
					<div className="mt-3 flex items-center gap-2">
						{canScrollChips ? (
							<button
								type="button"
								aria-label="Previous applicants"
								onClick={() => scrollChips(-1)}
								disabled={!chipEnds.left}
								className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#ECECEA] bg-white text-[#32302F] transition disabled:cursor-not-allowed disabled:opacity-40"
							>
								<ChevronLeft className="size-4" aria-hidden="true" />
							</button>
						) : null}
						<div
							ref={chipRefCb}
							onScroll={(e) => measureChips(e.currentTarget)}
							className="flex flex-1 scrollbar-none gap-2 overflow-x-auto"
						>
							{members.map((member) => (
								<ApplicantChip
									key={member.applicationId}
									member={member}
									selected={member.applicationId === selected.applicationId}
									onSelect={() => setSelectedId(member.applicationId)}
								/>
							))}
						</div>
						{canScrollChips ? (
							<button
								type="button"
								aria-label="Next applicants"
								onClick={() => scrollChips(1)}
								disabled={!chipEnds.right}
								className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#ECECEA] bg-white text-[#32302F] transition disabled:cursor-not-allowed disabled:opacity-40"
							>
								<ChevronRight className="size-4" aria-hidden="true" />
							</button>
						) : null}
					</div>
				</div>
			) : null}
			<div className="mt-5 rounded-2xl border border-[#F3F3F3] p-5">
				<p className="font-Regular text-[15px] text-[#32302F]">
					Timeline For {selected.name}
				</p>
				<ul className="mt-5">
					{selected.timeline.map((event, index) => (
						<EventRow
							key={event.key}
							event={event}
							isLast={index === selected.timeline.length - 1}
						/>
					))}
				</ul>
				{href ? (
					<Link
						to={href}
						className="mt-2 inline-flex items-center rounded-full bg-[#32302F] px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-black"
					>
						View Application
					</Link>
				) : null}
			</div>
		</>
	)
}

export function TimelinePanel({
	open,
	onOpenChange,
	header,
	members,
	defaultApplicationId,
	memberHref,
	hideViewApplicationFor,
	loading = false,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** Key-value card facts — the caller's already-loaded header data. Null
	 * while a lazy caller (dashboard) is still fetching. */
	header: {
		bedrooms: number | string | null | undefined
		moveInDate: string | null | undefined
		applicationDate: string
	} | null
	/** Every household member's pre-built timeline (loader `memberTimelines`). */
	members: MemberTimeline[]
	/** Chip selected on open — the page's own applicant (QA 2026-07-15). */
	defaultApplicationId: string
	/** Selected member → their individual detail URL ("View Application") —
	 * receives the member so cross-property households link correctly. */
	memberHref?: (member: MemberTimeline) => string
	/** Suppress "View Application" for this id (the page you're already on). */
	hideViewApplicationFor?: string
	/** Dashboard lazy fetch in flight — the shell opens immediately (a dead
	 * click reads as broken) with pulse placeholders instead of the body. */
	loading?: boolean
}) {
	const { formatShortDate } = useFormatDate()
	// Application Date ONLY (owner 2026-07-18) — Unit Type and Move-in Date are
	// commented out, not deleted, so they can be restored as-is. Parity doc §11.3
	// records the reduction.
	const keyValues: Array<[string, string]> = [
		// [
		// 	// Studio (0 bedrooms) must read "Studio", not "0 Bed" (TT2-494).
		// 	'Unit Type',
		// 	header ? (formatBedroomsShort(header.bedrooms) ?? '-') : '-',
		// ],
		// [
		// 	'Move-in Date',
		// 	header?.moveInDate ? formatShortDate(header.moveInDate) : '-',
		// ],
		[
			'Application Date',
			header ? formatShortDate(header.applicationDate) : '-',
		],
	]

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				className="w-full gap-0 overflow-y-auto border-l-0 bg-[#FBFDFD] p-6 sm:max-w-lg sm:rounded-l-[28px]"
			>
				<SheetHeader className="p-0">
					<SheetTitle className="font-calgary text-[28px] font-medium text-[#32302F]">
						Application Timeline
					</SheetTitle>
					<SheetDescription className="sr-only">
						Application milestones for each household member
					</SheetDescription>
				</SheetHeader>

				<div className="mt-5 rounded-2xl border border-[#F3F3F3] px-5 py-2">
					{keyValues.map(([label, value]) => (
						<div key={label} className="flex items-center justify-between py-1">
							<span className="font-Regular text-[15px] text-[#32302F]">
								{label}
							</span>
							<span className="text-[15px] text-[#747474]">{value}</span>
						</div>
					))}
					{loading ? (
						<div className="mt-5 space-y-3 rounded-2xl border border-[#F3F3F3] p-5">
							{[0, 1, 2, 3].map((row) => (
								<div key={row} className="flex items-center gap-4">
									<span className="size-10 shrink-0 animate-pulse rounded-full bg-[#F0F0EE]" />
									<span className="h-4 flex-1 animate-pulse rounded-full bg-[#F0F0EE]" />
								</div>
							))}
						</div>
					) : (
						<TimelineBody
							members={members}
							defaultApplicationId={defaultApplicationId}
							memberHref={memberHref}
							hideViewApplicationFor={hideViewApplicationFor}
						/>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}
